import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { from, of } from "rxjs";
import {
  catchError,
  concatMap,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import { AppState } from "../../app.state";
import { IDespacho, IResDespacho } from "../../interfaces/despacho";
import { LoadingService } from "../../loading/loading.service";
import { DespachoService } from "../../services/despacho/despacho.service";
import { EntradasService } from "../../services/entradas/entradas.service";
import { TemasService } from "../../services/temas/temas.service";
import {
  selectFilterTemasPorEmpresa,
  selectUsuario,
} from "../usuario/selector";
import {
  cancelarEntregableDespacho,
  consultarDespachos,
  consultarDespachosFail,
  consultarDespachosSuccess,
  ejeccionAccionDespachoError,
  ejeccionAccionDespachoSuccess,
  enviarNotificacionDespacho,
  enviarNotificacionTemas,
  enviarNotificacionTemasFailure,
  enviarNotificacionTemasSuccess,
  esNuevoDespacho,
  expandedTipoDespacho,
  getEntradasByIdEmpresa,
  getEntradasByIdEmpresaError,
  getEntradasByIdEmpresaSuccess,
  getFormulariosInfoVobo,
  guardarFormulariosInfoVobo,
  guardarNuevoDespacho,
  guardarNuevoDespachoFail,
  guardarNuevoDespachoSuccess,
  quitarTemaNotificarDespacho,
  resetDespacho,
  saveTemasDespachoFailure,
  seleccionarDespacho,
  setContador,
  setContadorByAcuerdo,
  setContadorSuccess,
  setEntradasByEmpresa,
  setEntradasEmpresaAndDespacho,
  terminarEntregablesDespacho,
  terminarSeguimientoDespacho,
  terminarTemaDespacho,
  terminarTemaDespachoFail,
  terminarTemaDespachoSuccess,
  updateCurrentDespacho,
  updateDespacho,
  updateStatusDespacho,
  validarTemasDespacho,
} from "./actions";
import {
  selectCamposValidosTemas,
  selectDespacho,
  selectDespachoActual,
  selectDespachoState,
  selectTemasANotificar,
  selectTemasVoBoInformativo,
} from "./selector";
import { ConfirmDialogComponent } from "../../confirm-dialog/confirm-dialog.component";
import {
  selectCatalogoCategorizacion,
  selectCatalogos,
  selectEmpresaById,
} from "../catalogos/selector";
import { AcuerdosService } from "../../services/acuerdos/acuerdos.service";
import { cargaUsuario } from "../usuario/actions";
import {
  ITerminarTemaDespacho,
  ITerminarTemaDespachoNotificacion,
} from "../../interfaces/acuerdos";
import { selectFormasEnvio } from "../formas-envio/selector";

@Injectable()
export class DespachoEffects {
  private stateCatalogos: any = this.store.pipe(select(selectCatalogos));

  constructor(
    private actions$: Actions,
    private despachoService: DespachoService,
    private store: Store<AppState>,
    private loadingService: LoadingService,
    public dialog: MatDialog,
    public entradasService: EntradasService,
    public temasService: TemasService,
    private acuerdosService: AcuerdosService
  ) {}

  consultarDespachos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(consultarDespachos),
      withLatestFrom(this.store.pipe(select(selectDespachoState))),
      mergeMap(([action, despachoState]) => {
        console.log("consultarDespachos... Estado del despacho", despachoState);

        this.loadingService.show();

        return this.despachoService.consultarTemasDespacho().pipe(
          map((response: IResDespacho) => {
            const state = this.stateCatalogos.state();
            console.log("Catalogos:", state);
            console.log("Respuesta del servicio:", response);

            const soloCorporativo = this.soloCorporativo(
              state.catalogos.catCategorizacion,
              state.usuario.temas
            );
            console.log("soloCorporativo:", soloCorporativo);

            const despachos: IDespacho[] = Object.keys(response)
              .filter((key) => (soloCorporativo ? key !== "otro" : true))
              .map((key) => ({
                ...response[key],
                expanded: false,
                marcarParaImpresion: false,
              }))
              .filter(
                (desp) => desp.VoBo.length > 0 || desp.Informativo.length > 0
              );
            this.loadingService.hide();

            const despachoActual = despachoState.despacho;

            let filterDespacho = despachos;
            if (despachoActual.expanded && despachoActual.id_empresa) {
              filterDespacho = despachos.map((des) => {
                const isSameCompany =
                  des.id_empresa === despachoActual.id_empresa;
                return isSameCompany ? { ...des, expanded: true } : des;
              });
            }

            this.store.dispatch(setContador());
            return consultarDespachosSuccess({
              despachos: filterDespacho,
            });
          }),
          catchError((error) => {
            this.loadingService.hide();
            console.error("Error al consultar despachos:", error);
            return of(consultarDespachosFail());
          })
        );
      })
    )
  );

  seleccionarDespacho$ = createEffect(() =>
    this.actions$.pipe(
      ofType(seleccionarDespacho),
      switchMap(({ despacho }) => {
        console.log("despacho", despacho);
        this.store.dispatch(expandedTipoDespacho({ currentExpanded: "" }));
        if (!despacho.expanded) {
          return of(
            setEntradasEmpresaAndDespacho({
              despacho,
              entradas: [],
            })
          );
        }

        this.loadingService.show();
        return this.store.pipe(
          select(selectFilterTemasPorEmpresa(despacho.id_empresa)),
          switchMap((filteredData) => {
            return this.entradasService.obtenerEntradas(filteredData).pipe(
              map((response) => {
                this.loadingService.hide();
                return setEntradasEmpresaAndDespacho({
                  despacho: despacho,
                  entradas: response.entradas ?? [],
                });
              }),
              catchError((error) => {
                this.loadingService.hide();
                return of(
                  setEntradasEmpresaAndDespacho({
                    despacho: despacho,
                    entradas: [],
                  })
                );
              })
            );
          }),
          catchError((error) => {
            this.loadingService.hide();
            console.error("Error en la lógica asincrónica:", error);
            return of(consultarDespachosFail()); // Manejo de errores con acción de falla
          })
        );
      })
    )
  );

  guardarFormulariosInfoVobo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(guardarFormulariosInfoVobo),
      withLatestFrom(this.store.pipe(select(selectDespachoActual))),
      tap(() => this.loadingService.show()),
      mergeMap(([{ form }, despachoActual]) => {
        console.log("form", form);
        console.log("form", despachoActual);
        const temas = [...form.informativo, ...form.vobo].map((tema) => ({
          ...tema,
          banEsImpreso:
            tema.banEsImpreso === 1 || tema.banEsImpreso ? true : false,
          indiceTema: tema.indice,
          indice_tema: tema.indice,
          searchText: "",
          selectedItem: false,
        }));
        if (temas.length > 0) {
          const payload = {
            temas: [...temas],
          };
         // this.store.dispatch(setEntradasByEmpresa({entradas:[]}));
          return this.temasService.editarTemas(payload).pipe(
            map((response) => {
              this.loadingService.hide();
              this.store.dispatch(
                updateDespacho({
                  vobo: form.vobo,
                  informativo: form.informativo,
                  id_empresa: despachoActual.id_empresa,
                })
              );
              return enviarNotificacionTemas();
            }),
            catchError((error) => {
              this.loadingService.hide();
              return of(saveTemasDespachoFailure());
            })
          );
        } else {
          this.loadingService.hide();
          return of(saveTemasDespachoFailure());
        }
      }),
      catchError((error) => {
        this.loadingService.hide();
        return of(terminarTemaDespachoFail());
      })
    )
  );

  guardarTemasDespacho$ = createEffect(() =>
    this.actions$.pipe(
      ofType(validarTemasDespacho),
      withLatestFrom(
        this.store.select(selectTemasVoBoInformativo),
        this.store.select(selectCamposValidosTemas)
      ),
      mergeMap(([action, temas, isValidForm]) => {
        this.loadingService.show();
        if (isValidForm) {
          const payload = {
            temas: [...temas],
          };
          return this.temasService.editarTemas(payload).pipe(
            map((response) => {
              this.loadingService.hide();
              return enviarNotificacionTemas();
            }),
            catchError((error) => {
              this.loadingService.hide();
              return of(saveTemasDespachoFailure());
            })
          );
        } else {
          this.loadingService.hide();
          return of(saveTemasDespachoFailure());
        }
      }),
      catchError((error) => of(saveTemasDespachoFailure()))
    )
  );

  enviardNotificacion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(enviarNotificacionTemas),
      withLatestFrom(this.store.pipe(select(selectUsuario))),
      mergeMap(([action, usuario]) => {
        this.loadingService.show();
        const temasSelector = selectTemasANotificar(usuario.nombre);
        return this.store.pipe(select(temasSelector)).pipe(
          take(1),
          mergeMap((temas) => {
            if (temas.length > 0) {
              return from(temas).pipe(
                concatMap((nuevoTema) =>
                  this.temasService.nuevoTema(nuevoTema).pipe(
                    map((response) => {
                      this.loadingService.hide();
                      return enviarNotificacionTemasSuccess();
                    }),
                    catchError((error) => {
                      return of(enviarNotificacionTemasFailure());
                    })
                  )
                )
              );
            } else {
              this.loadingService.hide();
              return of(enviarNotificacionTemasSuccess());
            }
          }),
          catchError((error) => {
            this.loadingService.hide();
            return of(enviarNotificacionTemasFailure());
          })
        );
      }),
      catchError((error) => of(enviarNotificacionTemasFailure()))
    )
  );

  quitarTemaNotificarDespacho$ = createEffect(() =>
    this.actions$.pipe(
      ofType(quitarTemaNotificarDespacho),
      withLatestFrom(this.store.pipe(select(selectDespacho))),
      tap(([_, selectDespacho]) => {
        console.log("Forma selectDespacho:", selectDespacho);
        this.loadingService.show();
      }),
      map(([{ quitarTema, temaEliminado }, selectDespacho]) => {
        //this.loadingService.hide();
        this.store.dispatch(ejeccionAccionDespachoSuccess());
        //   this.store.dispatch(resetDespacho());
        console.log("selectDespacho", selectDespacho);
        console.log("quitarTema", quitarTema.id_tema);
        console.log("temaEliminado", temaEliminado);
        let Informativo: any[] = [];
        let VoBo: any[] = [];
        if (selectDespacho.currentExpanded === "vobo") {
          VoBo = selectDespacho.despacho.VoBo.filter(
            (vb) => vb.id_tema !== quitarTema.id_tema
          );
        }
        if (selectDespacho.currentExpanded === "informativo") {
          Informativo = selectDespacho.despacho.Informativo.filter(
            (info) => info.id_tema !== quitarTema.id_tema
          );
        }
        const despacho: IDespacho = {
          expanded: true,
          ...selectDespacho.despacho,
          VoBo,
          Informativo,
        };

        console.log("Informativo", Informativo);
        console.log("VoBo", VoBo);

        this.store.dispatch(updateCurrentDespacho({ despacho }));
        this.store.dispatch(updateStatusDespacho({ status: "accionQuitar" }));

        return consultarDespachos();
      }),
      catchError((error) => {
        this.loadingService.hide();
        return of(ejeccionAccionDespachoError());
      })
    )
  );
  CancelarEntregable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cancelarEntregableDespacho),
      withLatestFrom(this.store.pipe(select(selectDespacho))),
      tap(() => this.loadingService.show()),
      map(([{ entregable }, selectedDespacho]) => {
        this.loadingService.hide();

        this.loadingService.hide();
        this.store.dispatch(ejeccionAccionDespachoSuccess());
        const despacho: IDespacho = {
          expanded: true,
          ...selectedDespacho.despacho,
        };

        this.store.dispatch(updateCurrentDespacho({ despacho }));
        this.store.dispatch(
          updateStatusDespacho({ status: "cancelarEntregable" })
        );
        return consultarDespachos();
      }),
      catchError((error) => {
        this.loadingService.hide();
        return of(ejeccionAccionDespachoError());
      })
    )
  );

  // terminarSeguimiento$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(terminarSeguimientoDespacho),
  //     withLatestFrom(this.store.pipe(select(selectDespacho))),
  //     tap(() => this.loadingService.show()),
  //     mergeMap(([{ seguimiento }, selectedDespacho]) =>
  //       from(this.despachoService.terminarSeguimiento(seguimiento)).pipe(
  //         map(() => {
  //           this.loadingService.hide();
  //           this.store.dispatch(ejeccionAccionDespachoSuccess());
  //           const despacho: IDespacho = {
  //             expanded: true,
  //             ...selectedDespacho.despacho,
  //           };

  //           this.store.dispatch(updateCurrentDespacho({ despacho }));
  //           this.store.dispatch(
  //             updateStatusDespacho({ status: "terminarSeguimiento" })
  //           );
  //           return consultarDespachos();
  //         }),
  //         catchError((error) => {
  //           this.loadingService.hide();
  //           return of(ejeccionAccionDespachoError());
  //         }),
  //         catchError((error) => {
  //           this.loadingService.hide();
  //           return of(ejeccionAccionDespachoError());
  //         })
  //       )
  //     )
  //   )
  // );

  terminarSeguimiento$ = createEffect(() =>
    this.actions$.pipe(
      ofType(terminarSeguimientoDespacho),
      withLatestFrom(this.store.pipe(select(selectDespacho))),
      tap(() => this.loadingService.show()),
      map(([{ seguimiento }, selectedDespacho]) => {
        // this.loadingService.hide();
        this.store.dispatch(ejeccionAccionDespachoSuccess());
        const despacho: IDespacho = {
          expanded: true,
          ...selectedDespacho.despacho,
        };
        this.store.dispatch(updateCurrentDespacho({ despacho }));
        this.store.dispatch(
          updateStatusDespacho({ status: "terminarSeguimiento" })
        );
        return consultarDespachos();
      }),
      catchError((error) => {
        this.loadingService.hide();
        return of(ejeccionAccionDespachoError());
      })
    )
  );

  terminarEntregables$ = createEffect(() =>
    this.actions$.pipe(
      ofType(terminarEntregablesDespacho),
      withLatestFrom(this.store.pipe(select(selectDespacho))),
      tap(() => this.loadingService.show()),
      map(([{ entregables }, selectedDespacho]) => {
        // this.loadingService.hide();
        this.store.dispatch(ejeccionAccionDespachoSuccess());
        const despacho: IDespacho = {
          expanded: true,
          ...selectedDespacho.despacho,
        };

        this.store.dispatch(updateCurrentDespacho({ despacho }));
        this.store.dispatch(
          updateStatusDespacho({ status: "terminarEntregable" })
        );
        return consultarDespachos();
      }),
      catchError((error) => {
        this.loadingService.hide();
        return of(ejeccionAccionDespachoError());
      })
    )
  );

  getEntradasByIdEmpresa$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getEntradasByIdEmpresa),
      switchMap(({ idEmpresa }) => {
        console.log("getEntradasByIdEmpresa idEmpresa", idEmpresa);

        this.loadingService.show();
        return this.store.pipe(
          select(selectFilterTemasPorEmpresa(idEmpresa)),
          switchMap((filteredData) => {
            return this.entradasService.obtenerEntradas(filteredData).pipe(
              map((response) => {
                this.loadingService.hide();
                return getEntradasByIdEmpresaSuccess({
                  entradas: response.entradas ?? [],
                });
              }),
              catchError((error) => {
                this.loadingService.hide();
                return of(getEntradasByIdEmpresaError());
              })
            );
          }),
          catchError((error) => {
            this.loadingService.hide();
            console.error("Error en la lógica asincrónica:", error);
            return of(getEntradasByIdEmpresaError()); // Manejo de errores con acción de falla
          })
        );
      })
    )
  );

  // guardarNuevoDespacho$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(guardarNuevoDespacho),
  //     switchMap(({ despacho }) => {
  //       console.log("despacho", despacho);

  //       return this.despachoService.agregarTemaDespacho(despacho).pipe(
  //         map((response) => {
  //           this.loadingService.hide();
  //           console.log("response guardarNuevoDespacho", response);
  //           if (response.error) {
  //             return guardarNuevoDespachoFail();
  //           } else if (!response.error) {
  //             this.showDialog("Éxito", "Tema agregado correctamente.");
  //             return enviarNotificacionDespacho({ despacho });
  //           } else {
  //             return guardarNuevoDespachoFail();
  //           }
  //         }),
  //         catchError((error) => {
  //           this.loadingService.hide();
  //           return of(guardarNuevoDespachoFail());
  //         })
  //       );
  //     })
  //   )
  // );

  // enviardNotificacionDespacho$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(enviarNotificacionDespacho),
  //     withLatestFrom(this.store.pipe(select(selectUsuario))),
  //     mergeMap(([{ type, despacho }, usuario]) => {
  //       this.loadingService.show();
  //       console.log("despacho", despacho);

  //       const empresaSelector = selectEmpresaById(despacho.id_empresa);
  //       return this.store.pipe(
  //         select(empresaSelector),
  //         take(1),
  //         switchMap((empresa: any) => {
  //           console.log("empresa", empresa);

  //           const req = {
  //             nombreUsuario: usuario.nombre,
  //             empresa: empresa ? empresa.descripcion : "", // Assuming empresa has a nombre property
  //             tipoDespacho:
  //               despacho.id_tipo_despacho == 1 ? "Informativo" : "VoBo", // Adjust as needed
  //             nombreTema: despacho.nombreTema,
  //             descripcionTema: "", // Assuming despacho has a descripcionTema property
  //             id_empresa: despacho.id_empresa,
  //             notificacion: "nuevoTema",
  //           };
  //           console.log("enviardNotificacionDespacho req ...", req);

  //           return this.temasService.nuevoTema(req).pipe(
  //             map((response) => {
  //               this.loadingService.hide();
  //               return enviarNotificacionTemasSuccess();
  //             }),
  //             catchError((error) => {
  //               this.loadingService.hide();
  //               return of(enviarNotificacionTemasFailure());
  //             })
  //           );
  //         }),
  //         catchError((error) => {
  //           this.loadingService.hide();
  //           return of(enviarNotificacionTemasFailure());
  //         })
  //       );
  //     }),
  //     catchError((error) => {
  //       this.loadingService.hide();
  //       return of(enviarNotificacionTemasFailure());
  //     })
  //   )
  // );

  private showDialog(title: string, message: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: "450px",
      data: {
        title,
        message,
        showButtons: {
          accept: true,
          cancel: false,
        },
        direction: "end",
        buttonsName: { cancel: "CANCELAR", accept: "ACEPTAR" },
      },
    });

    dialogRef.componentInstance.confirmAction.subscribe(() => {
      this.store.dispatch(consultarDespachos());
      this.store.dispatch(esNuevoDespacho());
    });
  }

  setContador$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cargaUsuario, setContador, setContadorByAcuerdo),
      switchMap((action) => {
        if (action.type == setContadorByAcuerdo.type) {
          console.log("setContadorByAcuerdo...", action.infoDespacho);
          console.log("setContadorByAcuerdo...", action.tipoAcuerdo);
          return this.acuerdosService
            .getAcuerdoActual(action.infoDespacho)
            .pipe(
              map((response) => {
                // this.loadingService.hide();
                return setContadorSuccess({
                  contador: {
                    acuerdo: response.acuerdo,
                    despacho: response.despacho,
                  },
                });
              })
            );
        } else {
          // this.loadingService.show();
          return this.acuerdosService.getContadores().pipe(
            map((response) => {
              console.log("response", response);

              // this.loadingService.hide();
              return setContadorSuccess({
                contador: {
                  acuerdo: response.acuerdo,
                  despacho: response.despacho,
                },
              });
            }),
            catchError((error) => {
              this.loadingService.hide();
              return of(
                setContadorSuccess({
                  contador: {
                    acuerdo: "",
                    despacho: "",
                  },
                })
              );
            })
          );
        }
      })
    )
  );

  // terminarTemaDespacho$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(terminarTemaDespacho),
  //     withLatestFrom(
  //       this.store.pipe(select(selectDespacho)),
  //       this.store.pipe(select(selectUsuario))
  //     ),
  //     tap(() => this.loadingService.show()),
  //     switchMap(
  //       ([{ temaDespacho, temaTerminado }, selectedDespacho, selectUser]) => {
  //         this.dialog.closeAll();

  //         return this.acuerdosService.terminarTemaDespacho(temaDespacho).pipe(
  //           switchMap((res) => {
  //             if (res.error) {
  //               return of(terminarTemaDespachoFail());
  //             }
  //             return this.acuerdosService
  //               .notificacionTerminarTema(temaTerminado)
  //               .pipe(
  //                 map((data) => {
  //                   console.log("data", data);

  //                   if (!data.error) {
  //                     console.log(
  //                       "selectUser terminarTemaDespacho",
  //                       selectUser
  //                     );
  //                     console.log(
  //                       "selectedDespacho terminarTemaDespacho",
  //                       selectedDespacho
  //                     );
  //                     console.log(
  //                       "temaTerminado terminarTemaDespacho",
  //                       temaTerminado
  //                     );

  //                     this.loadingService.hide();

  //                     const dialogRef = this.dialog.open(
  //                       ConfirmDialogComponent,
  //                       {
  //                         width: "450px",
  //                         data: {
  //                           title: "Éxito",
  //                           message: "El tema ha sido terminado",
  //                           showButtons: {
  //                             accept: true,
  //                             cancel: false,
  //                           },
  //                           direction: "end",
  //                           buttonsName: {
  //                             cancel: "CANCELAR",
  //                             accept: "CERRAR",
  //                           },
  //                         },
  //                       }
  //                     );

  //                     dialogRef.componentInstance.confirmAction.subscribe(
  //                       () => {
  //                         this.loadingService.hide();
  //                         this.store.dispatch(ejeccionAccionDespachoSuccess());
  //                         const despacho: IDespacho = {
  //                           expanded: true,
  //                           ...selectedDespacho.despacho,
  //                         };

  //                         this.store.dispatch(
  //                           updateCurrentDespacho({ despacho })
  //                         );
  //                         this.store.dispatch(
  //                           updateStatusDespacho({ status: "terminar" })
  //                         );
  //                         this.store.dispatch(consultarDespachos());
  //                       }
  //                     );

  //                     dialogRef.componentInstance.cancelAction.subscribe(() => {
  //                       return terminarTemaDespachoSuccess();
  //                     });
  //                     return terminarTemaDespachoSuccess();
  //                   }

  //                   return terminarTemaDespachoSuccess();
  //                 }),
  //                 catchError(() => {
  //                   this.loadingService.hide();
  //                   return of(terminarTemaDespachoFail());
  //                 })
  //               );
  //           }),
  //           catchError((error) => {
  //             this.loadingService.hide();
  //             return of(terminarTemaDespachoFail());
  //           })
  //         );
  //       }
  //     ),
  //     catchError((error) => {
  //       this.loadingService.hide();
  //       return of(terminarTemaDespachoFail());
  //     })
  //   )
  // );

  private showDialogSucces(title: string, message: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: "450px",
      data: {
        title,
        message,
        showButtons: {
          accept: true,
          cancel: false,
        },
        direction: "end",
        buttonsName: { cancel: "CANCELAR", accept: "CERRAR" },
      },
    });

    dialogRef.componentInstance.confirmAction.subscribe(() => {
      this.store.dispatch(consultarDespachos());
    });
  }

  soloCorporativo(categorias: any[], temas: any): boolean {
    console.log("categorias", JSON.stringify(categorias));
    console.log("user", JSON.stringify(temas));
    const allowedCategories = new Set(["C", "CC"]);
    return Object.keys(temas).every((categoryId: string) => {
      const category = categorias.find((cat) => cat.id === categoryId);
      return category ? allowedCategories.has(category.descripcion) : false;
    });
  }
}
